import React, { useState } from 'react';
import Layout from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { PALETTES } from '../lib/themes';
import { Check, PaintBrush, Sun, Moon, Buildings, User } from '@phosphor-icons/react';
import { updateCompanyBranding } from '../lib/api';
import { toast } from 'sonner';

export default function AppearanceSettings() {
  const { theme, setTheme, palette, userPalette, companyPalette, setPalette, clearUserPalette, setCompanyPalette } = useTheme();
  const { user } = useAuth();
  const isOwner = user?.company_role === 'owner' || user?.company_role === 'admin' || user?.role === 'admin';
  const [savingCompany, setSavingCompany] = useState(false);

  const saveCompanyPalette = async (key) => {
    setSavingCompany(true);
    try {
      await updateCompanyBranding(key);
      setCompanyPalette(key);
      toast.success(`Company default set to ${PALETTES[key].label}. New users see this on first load.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save company default');
    } finally {
      setSavingCompany(false);
    }
  };

  return (
    <Layout>
      <div data-testid="appearance-settings-page" className="p-6 max-w-4xl">
        <div className="mb-6">
          <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-widest mb-2">
            <PaintBrush size={14} weight="bold" /> Settings
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Appearance</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customize how Stockaris looks. Your selection overrides the company default for this browser.
          </p>
        </div>

        {/* Theme mode */}
        <section className="mb-10" data-testid="appearance-theme-section">
          <h2 className="text-base font-semibold text-foreground mb-1">Theme mode</h2>
          <p className="text-sm text-muted-foreground mb-4">Light keeps the warehouse-floor feel calm; dark is gentler on the eyes during night-shift dispatch ops.</p>
          <div className="flex gap-3">
            <ModeOption testid="appearance-mode-light" label="Light" active={theme === 'light'} onClick={() => setTheme('light')} icon={<Sun size={18} weight="duotone" />} />
            <ModeOption testid="appearance-mode-dark" label="Dark" active={theme === 'dark'} onClick={() => setTheme('dark')} icon={<Moon size={18} weight="duotone" />} />
          </div>
        </section>

        {/* Palette */}
        <section data-testid="appearance-palette-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <h2 className="text-base font-semibold text-foreground">Accent palette</h2>
            {companyPalette && (
              <span data-testid="company-default-pill" className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground bg-muted px-2 py-1 rounded-full">
                <Buildings size={12} weight="bold" /> Company default: {PALETTES[companyPalette]?.label || companyPalette}
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Pick a brand accent — affects buttons, highlights, charts, and active sidebar rail across the entire app.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(PALETTES).map(([key, p]) => {
              const isActive = palette === key;
              const isUserChoice = userPalette === key;
              const isCompanyDefault = companyPalette === key;
              return (
                <div
                  key={key}
                  data-testid={`appearance-palette-${key}`}
                  className={`relative text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                    isActive ? 'border-brand bg-card shadow-md' : 'border-border bg-card hover:border-ring/40 hover:shadow-sm'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setPalette(key)}
                    className="absolute inset-0 w-full h-full rounded-lg"
                    aria-label={`Use ${p.label} palette`}
                  />
                  <div className="relative pointer-events-none flex items-start gap-3">
                    <span
                      className="w-10 h-10 rounded-md shrink-0 ring-1 ring-border"
                      style={{ backgroundColor: p.swatch }}
                      aria-hidden
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <span className="font-semibold text-foreground">{p.label}</span>
                        <div className="flex items-center gap-1 flex-wrap">
                          {isUserChoice && (
                            <span data-testid={`appearance-palette-${key}-user-active`} className="inline-flex items-center gap-1 text-[10px] font-semibold text-brand">
                              <User size={10} weight="bold" /> Your override
                            </span>
                          )}
                          {isCompanyDefault && !isUserChoice && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
                              <Buildings size={10} weight="bold" /> Company
                            </span>
                          )}
                          {isActive && (
                            <span data-testid={`appearance-palette-${key}-active`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand">
                              <Check size={12} weight="bold" /> Active
                            </span>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{p.description}</p>
                    </div>
                  </div>
                  <div className="relative flex items-center gap-2 mt-3 pt-3 border-t border-border pointer-events-none">
                    <span className="px-3 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: p.swatch, color: '#fff' }}>Button</span>
                    <span className="text-xs font-semibold" style={{ color: p.swatch }}>Link</span>
                    <span className="ml-auto text-[10px] font-mono text-muted-foreground">{p.swatch}</span>
                  </div>

                  {/* Owner-only company-default action */}
                  {isOwner && (
                    <div className="relative mt-3 pt-3 border-t border-border flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        {isCompanyDefault ? 'Set as company default ✓' : 'Make this the company default'}
                      </span>
                      {!isCompanyDefault && (
                        <button
                          type="button"
                          onClick={() => saveCompanyPalette(key)}
                          disabled={savingCompany}
                          data-testid={`appearance-set-company-default-${key}`}
                          className="text-[10px] font-semibold text-brand hover:underline disabled:opacity-50 uppercase tracking-wider"
                        >
                          Set →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {userPalette && (
            <div className="mt-4 flex items-center justify-between gap-2 p-3 bg-muted rounded-md text-xs">
              <span className="text-muted-foreground">
                You're overriding the company default. Reset to fall back to <strong className="text-foreground">{PALETTES[companyPalette]?.label || PALETTES.default.label}</strong>.
              </span>
              <button
                type="button"
                onClick={clearUserPalette}
                data-testid="appearance-clear-user-palette"
                className="text-brand font-semibold hover:underline whitespace-nowrap"
              >
                Reset to company default
              </button>
            </div>
          )}
        </section>

        <div className="mt-8 text-xs text-muted-foreground" data-testid="appearance-help">
          Tip — your selection is saved to <code className="font-mono px-1 bg-muted rounded text-[11px]">localStorage</code>. Company default is stored on the server and applies to all teammates by default.
        </div>
      </div>
    </Layout>
  );
}

function ModeOption({ testid, label, active, onClick, icon }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-all ${
        active
          ? 'border-brand bg-brand text-brand-foreground'
          : 'border-border bg-card text-foreground hover:bg-accent'
      }`}
    >
      {icon}
      {label}
      {active && <Check size={14} weight="bold" className="ml-1" />}
    </button>
  );
}
